import { api } from './index';

// Types for bid submission management
export interface BidSubmission {
  id: string;
  tenderId: string;
  companyName: string;
  priceForMRI?: number;
  priceForCT?: number;
  totalPrice: number;
  representativeName?: string;
  representativeTitle?: string;
  representativePhone?: string;
  representativeEmail?: string;
  hasTOR: boolean;
  hasFinancialStatement: boolean;
  hasTaxClearance: boolean;
  hasInsurance: boolean;
  hasExperience: boolean;
  hasTechnicalProposal: boolean;
  hasFinancialProposal: boolean;
  status?: 'SAVED' | 'SUBMITTED';
  qualificationStatus?: 'PENDING' | 'QUALIFIED' | 'DISQUALIFIED';
  qualificationNotes?: string | null;
  evaluationNotes?: string | null; // Overall evaluation notes/justifications
  bidderId?: string | null;
  vendorId?: string | null;
  submittedById?: string;
  submittedAt?: string;
  updatedAt: string;
  deletedAt?: string;
  submittedBy?: {
    id: string;
    fullName: string;
  };
  documents?: Array<{
    id: string;
    fileName: string;
    originalName: string;
    documentType: string;
    title?: string;
    description?: string;
    uploadedAt: string;
  }>;
  bidItems?: Array<{
    id: string;
    tenderItemId: string;
    quantity: number;
    unit: string;
    price: number;
    order: number;
    tenderItem?: {
      id: string;
      itemName: string;
      description?: string;
    };
  }>;
  tender?: {
    id: string;
    referenceNo: string;
    description: string;
    submissionDeadline?: string;
    stage?: string;
  };
}

export interface CreateBidSubmissionDto {
  companyName: string;
  priceForMRI?: number;
  priceForCT?: number;
  totalPrice: number;
  representativeName?: string;
  representativeTitle?: string;
  representativePhone?: string;
  representativeEmail?: string;
  companyAddress?: string;
  registrationNumber?: string;
  taxId?: string;
  status?: 'SAVED' | 'SUBMITTED'; // For draft vs submitted
  bidItems?: Array<{
    tenderItemId: string;
    quantity: number;
    unit: string;
    price: number;
    order: number;
  }>;
  // Document requirements - using a more flexible approach
  [key: string]: any; // This allows for dynamic document type fields like hasCustom, hasQuotation, etc.
}

export interface UpdateBidSubmissionDto extends Partial<CreateBidSubmissionDto> {}

export interface BidderSearchResult {
  id: string;
  companyName: string;
  representativeName?: string;
  representativeEmail?: string;
  registrationNumber?: string;
  tenderCount: number;
}

// API functions
export const bidSubmissionApi = {
  // Create or update bid submission (saves as draft if status is SAVED)
  createBidSubmission: async (tenderId: string, data: CreateBidSubmissionDto, useVendorAuth: boolean = false): Promise<BidSubmission> => {
    const response = await api.post(`/tenders/${tenderId}/bid-submissions`, data, { useVendorAuth });
    return response.data;
  },

  // Get draft bid submission for current vendor
  getDraftBidSubmission: async (tenderId: string, useVendorAuth: boolean = false): Promise<BidSubmission | null> => {
    const response = await api.get(`/tenders/${tenderId}/bid-submissions/draft`, { useVendorAuth });
    return response.data;
  },

  // List bid submissions for a tender
  listBidSubmissions: async (tenderId: string): Promise<BidSubmission[]> => {
    const response = await api.get(`/tenders/${tenderId}/bid-submissions`);
    return response.data;
  },

  // Get bid submission by ID
  getBidSubmissionById: async (tenderId: string, submissionId: string, useVendorAuth: boolean = false): Promise<BidSubmission> => {
    const response = await api.get(`/tenders/${tenderId}/bid-submissions/${submissionId}`, { useVendorAuth });
    return response.data;
  },

  // Update bid submission
  updateBidSubmission: async (tenderId: string, submissionId: string, data: UpdateBidSubmissionDto, useVendorAuth: boolean = false): Promise<BidSubmission> => {
    const response = await api.put(`/tenders/${tenderId}/bid-submissions/${submissionId}`, data, { useVendorAuth });
    return response.data;
  },

  // Remove bid submission
  removeBidSubmission: async (tenderId: string, submissionId: string): Promise<void> => {
    await api.delete(`/tenders/${tenderId}/bid-submissions/${submissionId}`);
  },

  // Search existing bidders (reuse from bidder API)
  searchExistingBidders: async (query: string, limit: number = 10): Promise<BidderSearchResult[]> => {
    const response = await api.get(`/tenders/bidders/search?query=${encodeURIComponent(query)}&limit=${limit}`);
    return response.data;
  },

  // Get vendor's bid submissions
  getVendorBidSubmissions: async (): Promise<BidSubmission[]> => {
    const response = await api.get(`/vendors/bid-submissions`, { useVendorAuth: true });
    return response.data;
  },
};
